import {computed, ref, Ref, watch} from 'vue'
import type {DataColumn, DataError, DataProvider, DataRow} from '@hantera/design-system'
import type {Role, RolesListParams} from '../../../backend/iam.types'
import type iam from '../../../backend/iam'

export class RolesDataProvider implements DataProvider {
  private _rows = ref<DataRow[]>([])
  private _isLoading = ref(false)
  private _allLoaded = ref(false)
  private _entries: Role[] = []
  private _lastCursor: string | null = null
  private _errors = ref<DataError[]>([])

  public readonly rows = computed(() => this._rows.value)
  public readonly isLoading = computed(() => this._isLoading.value)
  public readonly allLoaded = computed(() => this._allLoaded.value)
  public readonly errors = computed(() => this._errors.value.length > 0 ? this._errors.value : undefined)

  public readonly columns = computed<DataColumn[]>(() => [
    {
      key: 'name',
      label: this.t('columns.name'),
      valueIdx: 0,
      canSort: true,
      canFilter: false,
      sort: this.filters.value.sortBy === 'name' ? this.filters.value.sortDirection : undefined,
      values: {
        type: 'text',
        isArray: false,
      },
    },
    {
      key: 'description',
      label: this.t('columns.description'),
      valueIdx: 1,
      canSort: true,
      canFilter: false,
      sort: this.filters.value.sortBy === 'description' ? this.filters.value.sortDirection : undefined,
      values: {
        type: 'text',
        isArray: false,
      },
    },
  ])

  public readonly totalCount = computed(() => {
    if (this.rows.value.length === 0) return undefined
    return {
      count: this.rows.value.length,
      cap: this._allLoaded.value ? undefined : this.rows.value.length,
    }
  })

  constructor(
    private filters: Ref<RolesListParams>,
    private iamService: ReturnType<typeof iam>,
    private t: (key: string, params?: Record<string, any>) => string,
  ) {
    // Watch filters and refresh when they change
    watch(
      filters,
      () => {
        this.refresh()
      },
      { deep: true },
    )
  }

  public async refresh(): Promise<void> {
    this._isLoading.value = true
    this._errors.value = []
    this._entries = []
    this._rows.value = []
    this._lastCursor = null
    this._allLoaded.value = false

    try {
      const response = await this.iamService.getRoles({
        ...this.filters.value,
        after: undefined,
        before: undefined,
      })

      this._entries = response.roles
      this._rows.value = this._entries.map((r) => this.entryToRow(r))
      this._lastCursor = response.lastCursor ?? null
      this._allLoaded.value = !response.hasMore
    } catch (error: any) {
      console.error('Failed to load roles:', error)
      this._errors.value = [
        {
          code: error.response?.status?.toString() || 'UNKNOWN',
          message: error.response?.data?.message || 'Failed to load roles',
        },
      ]
    } finally {
      this._isLoading.value = false
    }
  }

  public async loadMore(): Promise<void> {
    if (this._isLoading.value || this._allLoaded.value || !this._lastCursor) {
      return
    }

    this._isLoading.value = true
    this._errors.value = []

    try {
      const response = await this.iamService.getRoles({
        ...this.filters.value,
        after: this._lastCursor,
        before: undefined,
      })

      this._entries.push(...response.roles)
      this._rows.value = this._entries.map((r) => this.entryToRow(r))
      this._lastCursor = response.lastCursor ?? null
      this._allLoaded.value = !response.hasMore
    } catch (error: any) {
      console.error('Failed to load more roles:', error)
      this._errors.value = [
        {
          code: error.response?.status?.toString() || 'UNKNOWN',
          message: error.response?.data?.message || 'Failed to load more roles',
        },
      ]
    } finally {
      this._isLoading.value = false
    }
  }

  public setSort(column: DataColumn, direction?: 'asc' | 'desc'): void {
    if (column.key === 'name' || column.key === 'description') {
      this.filters.value.sortBy = direction ? (column.key as 'name' | 'description') : undefined
      this.filters.value.sortDirection = direction
    }
  }

  public getRowHue(row: DataRow) {
    return row.key.startsWith('system:') ? '--color-hue-gray' : undefined
  }

  private entryToRow(role: Role): DataRow {
    return {
      key: role.key,
      values: [role.key, role.description || ''],
    }
  }
}
